import { Paper, Stack } from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { ReactElement, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { ChipFilterBar } from '@/components/FilterChips/ChipFilterBar';
import { useLiveGraphQL } from '@/hooks/useLiveGraphQL';
import { useCalendarDay, useNow } from '@/hooks/useNow';
import { alert } from '@/services/alert';
import { getRateLimitAlert, isMedplumRateLimitError } from '@/utils/medplumRateLimit';
import { ALL_PRE_CONSULTATION_APPOINTMENTS_QUERY } from '../graphql';
import { usePatientListFilters } from '../hooks/usePatientListFilters';
import { usePacQueueHydration } from '../hooks/usePacQueueHydration';
import {
  buildPreConsultationRows,
  PacQueueAppointmentResult,
  PreConsultationLeadRow,
} from '../services/preConsultationRows';
import {
  buildPreConsultationFilter,
  isInPreConsultationScope,
  preConsultationScope,
  preConsultationSubscriptionParams,
  PRE_CONSULTATION_CHIPS,
} from '../services/preConsultationFilters';
import { COHORT_ROW_CAP, QUEUE_PAGE_SIZE, shouldWarnTruncated } from '../services/cohortLoading';
import { lastCommunicationFloor } from '../services/pacQueueHydration';
import { useCohortTotal } from '../hooks/useCohortTotal';
import { CohortTruncationNotice } from './CohortTruncationNotice';
import { ListRangeControl } from '@/components/Hub/ListRangeControl';
import { getCiQueueRowProps } from './listRowStyle';
import { usePreConsultationColumns } from './preConsultationColumns';

/**
 * The pre-consultation CI queue.
 *
 * A self-contained tab rather than another branch inside `usePatientList`. That
 * hook resolves a cohort by stage tag and hands one shape to a shared table; this
 * tab roots on the intro call, carries its own scope control and chips, and needs
 * none of that machinery. Folding it in would mean widening six other tabs'
 * code path to express one tab's query.
 */

export interface PreConsultationTabProps {
  /** Injectable for tests; defaults to a clock that keeps ticking. */
  now?: Date;
}

export function PreConsultationTab({ now }: PreConsultationTabProps): ReactElement {
  const navigate = useNavigate();
  // Ticks, so the range rolls over at midnight without a reload (BAR-2708).
  const resolvedNow = useNow(now);
  const today = useCalendarDay(resolvedNow);

  const filters = usePatientListFilters<PreConsultationLeadRow>({
    resetKey: 'all-pre-consultation',
    chipDefinitions: PRE_CONSULTATION_CHIPS,
    now: resolvedNow,
  });

  const scope = useMemo(() => preConsultationScope(filters.range, filters.presetId), [filters.range, filters.presetId]);
  const variables = useMemo(() => ({ filter: buildPreConsultationFilter(scope) }), [scope]);
  const subscriptionParams = useMemo(() => preConsultationSubscriptionParams(), []);

  // `useLiveGraphQL` constrains its generic to `Resource`, which requires the
  // `status` and `participant` a GraphQL selection does not guarantee. Fetch as
  // the base resource and narrow at the boundary rather than lying about either.
  const { data, loading, error, scrollPagination, cohortTruncated } = useLiveGraphQL(
    ALL_PRE_CONSULTATION_APPOINTMENTS_QUERY,
    variables,
    {
      enablePagination: true,
      pageSize: QUEUE_PAGE_SIZE,
      autoLoadUpTo: COHORT_ROW_CAP,
      subscriptionParams,
    }
  );

  // Only the notice needs the real total, and only when the cap bit.
  const cohortTotal = useCohortTotal('Appointment', cohortTruncated ? variables.filter : undefined);

  useEffect(() => {
    if (error && isMedplumRateLimitError(error)) {
      alert.warning({
        ...getRateLimitAlert(
          `The system temporarily limited how fast we can load the intro-call queue.
          Wait a few seconds before trying again.`
        ),
      });
    }
  }, [error]);

  // The subscription is type-only, so what it merges is scoped here the way
  // the query scoped the page (status, range).
  const appointments = useMemo(
    () =>
      error
        ? []
        : (data as PacQueueAppointmentResult[]).filter((appointment) => isInPreConsultationScope(appointment, scope)),
    [data, error, scope]
  );

  // Everything hanging off the patient arrives in a handful of bulk searches
  // rather than one nested chain per patient per type — see
  // `services/pacQueueHydration.ts`.
  const patientRefs = useMemo(
    () => [
      ...new Set(
        appointments
          .flatMap((appointment) => appointment.participant ?? [])
          .map((participant) => participant.actor?.resource)
          .filter((resource) => resource?.resourceType === 'Patient')
          .map((resource) => `Patient/${(resource as { id?: string }).id}`)
      ),
    ],
    [appointments]
  );

  const introCallRefs = useMemo(
    () =>
      appointments.map((appointment) => `Appointment/${appointment.id}`).filter((ref) => !ref.endsWith('undefined')),
    [appointments]
  );

  const hydration = usePacQueueHydration(
    patientRefs,
    {
      consult: true,
      tasks: true,
      introCallNoteFor: introCallRefs,
      // The Location column's last resort, for a lead with no charted call.
      eligibility: true,
      lastCommunicationSince: lastCommunicationFloor(today),
    },
    `all-pre-consultation:${variables.filter}`
  );

  const rows = useMemo(
    () => buildPreConsultationRows(appointments, hydration, resolvedNow),
    [appointments, hydration, resolvedNow]
  );

  const visibleRows = useMemo(() => filters.chips.apply(rows), [filters.chips, rows]);
  const columns = usePreConsultationColumns(today);

  const table = useMantineReactTable({
    columns,
    data: visibleRows,
    // Sorted by consult date ascending — soonest first. The Intro call column
    // would be the more natural default and was, but Intake 1.1 left it empty on
    // every production row, and a default sort on an always-empty column silently
    // leaves the queue in query order.
    initialState: { density: 'xs' as const, sorting: [{ id: 'consultDate', desc: false }] },
    state: { isLoading: loading || hydration.loading },
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableRowActions: false,
    enableRowSelection: false,
    enableRowVirtualization: true,
    enableToolbarInternalActions: false,
    mantineTableProps: { highlightOnHover: true },
    mantineTableBodyRowProps: ({ row }) =>
      getCiQueueRowProps(row.original, (patientId) => navigate(`/Patient/${patientId}/timeline`)),
    mantinePaperProps: {
      shadow: 'sm',
      withBorder: true,
      style: { height: '100%', display: 'flex', flexDirection: 'column' },
    },
    mantineTableContainerProps: scrollPagination?.tableContainerProps,
    mantineTableHeadCellProps: { style: { fontWeight: '500' } },
  });

  return (
    <Stack gap="sm" style={{ flex: 1, overflow: 'hidden' }}>
      {/*
        The range control sits left of the count because it is a scope, not one
        more thing switched on. The count lives inside ChipFilterBar and is fed
        the rows actually on screen, so it can never disagree with the filters.
      */}
      <ListRangeControl presetId={filters.presetId} value={filters.range} onChange={filters.setRange} />
      {/*
        The count is every row in the date-scoped cohort that passes the chips.
        The tab loads the whole cohort rather than a page, so it no longer
        describes only what a reader has scrolled past (BAR-2738). Where the row
        cap bites, the notice above says so — that is the one case where the
        count is still a prefix, and it must not be silent.
      */}
      {shouldWarnTruncated(cohortTruncated, rows.length, cohortTotal) && (
        <CohortTruncationNotice loaded={rows.length} total={cohortTotal} />
      )}
      <ChipFilterBar state={filters.chips} count={visibleRows.length} />
      <Paper style={{ flex: 1, overflow: 'hidden' }}>
        <MantineReactTable table={table} />
      </Paper>
    </Stack>
  );
}
